import { listViewStore } from 'owa-mail-list-store';
import { getActionSourceFromCommandingViewMode } from 'owa-command-ribbon';
import {
    lazyAddCategoriesFromTable,
    lazyRemoveCategoriesFromTable,
    lazyClearCategoriesFromTable,
} from 'owa-mail-triage-action';
import { lazyShowCategoryDialog } from 'owa-categories';
import { lazyAddFavoriteCategory } from 'owa-favorites';
import setInitialCategoryDialogViewState from 'owa-categories/lib/actions/setInitialCategoryDialogViewState';
import { lazyMountAndShowFullOptions } from 'owa-options-view';
import { lazyResetFocus } from 'owa-mail-focus-manager';
import type CategoryActionSource from 'owa-categories/lib/utils/CategoryActionSource';

/**
 * Function to handle a category clicked.
 *
 * @param categoryName: name of category
 * @param isChecked: whether category is currently applied on the item(s)
 *
 */
export const onCategoryClicked = (categoryName: string, isChecked: boolean) => {
    // Dismiss menu first and then proceed with click handler.
    lazyResetFocus.importAndExecute();

    const tableViewId = listViewStore.selectedTableViewId;
    const tableView = listViewStore.tableViews.get(tableViewId)!;
    const rowKeys = tableView.isInVirtualSelectAllMode
        ? tableView.rowKeys
        : [...tableView.selectedRowKeys.keys()];

    if (isChecked) {
        // Checked category is clicked, remove category.
        lazyRemoveCategoriesFromTable.importAndExecute(
            rowKeys,
            tableView,
            [categoryName],
            getActionSourceFromCommandingViewMode()
        );
    } else {
        // Unchecked category is clicked, add category.
        lazyAddCategoriesFromTable.importAndExecute(
            rowKeys,
            tableView,
            [categoryName],
            getActionSourceFromCommandingViewMode()
        );
    }
};

/**
 * Shows the Category Dialog to allow user to create a new category
 */
export const onNewCategoryClicked = (categoryName: string | undefined) => {
    const tableViewId = listViewStore.selectedTableViewId;
    const tableView = listViewStore.tableViews.get(tableViewId)!;
    const rowKeys = tableView.isInVirtualSelectAllMode
        ? tableView.rowKeys
        : [...tableView.selectedRowKeys.keys()];

    const addFavoriteCategory = (categoryId: string, actionSource: CategoryActionSource) => {
        lazyAddFavoriteCategory
            .import()
            .then(addFavoriteCategory => addFavoriteCategory(categoryId, actionSource));
    };

    const onAfterCreateNewCategory = (category?: string) => {
        // When a new category is created, we should apply it optimistically to the selected items
        lazyAddCategoriesFromTable.importAndExecute(
            rowKeys,
            tableView,
            category ? [category] : [],
            getActionSourceFromCommandingViewMode()
        );
    };

    setInitialCategoryDialogViewState('new', categoryName);
    lazyShowCategoryDialog.importAndExecute({
        actionSource: getActionSourceFromCommandingViewMode(),
        addFavoriteCategory: addFavoriteCategory,
        onAfterCreateNewCategory: onAfterCreateNewCategory,
    });
    lazyResetFocus.importAndExecute();
};

/**
 * Clears all categories on the selected mail item(s)
 */
export const onClearCategoriesClicked = () => {
    lazyResetFocus.importAndExecute();

    const tableViewId = listViewStore.selectedTableViewId;
    const tableView = listViewStore.tableViews.get(tableViewId)!;
    const rowKeys = tableView.isInVirtualSelectAllMode
        ? tableView.rowKeys
        : [...tableView.selectedRowKeys.keys()];

    lazyClearCategoriesFromTable.importAndExecute(
        rowKeys,
        tableView,
        getActionSourceFromCommandingViewMode()
    );
};

/**
 * Clears all categories on the selected mail item(s)
 */
export const onManageCategoriesClicked = () => {
    lazyMountAndShowFullOptions.importAndExecute('general', 'categories');
};
